import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AuthMethod, Contact } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { UsersMutations } from '@frontend/api-users';
import {
  useMFAContext,
  MFAFormStep,
  SelectMFAMethod,
  AddMFAAddress,
  VerifyMFAAddress,
  AddMfaMethodButton,
} from '@frontend/account';
import { navigateToUrl } from '@frontend/auth';
import { getUser } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Text, CheckboxField, useControlledField } from '@frontend/design-system';
import { MFAPromptTitle } from '../../components/mfa/mfa-prompt-title';
import { SkipButton } from '../../components/mfa/skip-button';

type MFAPromptFlowProps = {
  redirectTo?: string;
  mfaMethods?: Contact[];
};

const IMPRESSION_TYPE = 'mfa_post_login_prompt';

/**
 * The flow for the post-login MFA prompt. Resembles the MFA flow in the settings page (libs/features/account/src/components/users-2.0/mfa/mfa-form.tsx)
 */
export function MFAPromptFlow({ redirectTo = appConfig.DEFAULT_REDIRECT, mfaMethods }: MFAPromptFlowProps) {
  const { t } = useTranslation('account');
  const step = useMFAContext((ctx) => ctx.step);
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  const { mutateAsync: enableMFAMutation, isLoading } = UsersMutations.useEnableMFA();
  const { mutateAsync: createImpressionMutation } = UsersMutations.useCreateImpression();
  const { mutateAsync: optOutImpressionMutation } = UsersMutations.useOptOutImpression();

  // on component mount, record an impression for the MFA prompt
  // we use this data to determine server side if a user should see the prompt again
  // this ensures we don't spam the user with repeated prompts
  useEffect(() => {
    async function recordImpression() {
      try {
        await createImpressionMutation({ type: IMPRESSION_TYPE });
      } catch (error) {
        sentry.error({
          error: `Error recording MFA prompt impression - ${error}`,
          team: 'users-infra',
          topic: 'auth',
        });
      }
    }
    recordImpression();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // enables MFA and navigates to the redirect URL
  const onVerifySuccess = useCallback(async () => {
    const userID = getUser()?.userID;
    // enabling MFA is a separate step from adding a new method
    // so we call this once they've verified the new method
    if (userID) {
      await enableMFAMutation({ userId: userID });
    }
    // top-level navigation to the new app
    navigateToUrl(redirectTo, { reason: 'MFA verify success' });
  }, [enableMFAMutation, redirectTo]);

  const hasMFAMethods = mfaMethods && mfaMethods.length > 0;
  const defaultMethod = hasMFAMethods && mfaMethods[0];

  const handleSkip = useCallback(async () => {
    try {
      if (neverShowAgain) {
        await optOutImpressionMutation({ type: IMPRESSION_TYPE });
      }
    } catch (error) {
      sentry.error({
        error: `Error with never show again for MFA prompt - ${error}`,
        team: 'users-infra',
        topic: 'auth',
      });
    } finally {
      navigateToUrl(redirectTo, { reason: 'MFA skip' });
    }
  }, [neverShowAgain, optOutImpressionMutation, redirectTo]);

  const checkboxField = useControlledField({
    onChange: (value) => {
      setNeverShowAgain(value);
    },
    type: 'checkbox',
    value: neverShowAgain,
  });

  return (
    <section
      css={css`
        max-width: 450px;
        width: 100%;
      `}
    >
      <MFAPromptTitle />

      <MFAFormStep currentStep={step} step='initial'>
        <Text>
          {t(
            "Help protect your patients' sensitive information by enabling an extra security step. You'll get a code by email or text to ensure only authorized access."
          )}
        </Text>
        {hasMFAMethods && defaultMethod && (
          <AddMfaMethodButton
            icon={defaultMethod.type === AuthMethod.EMAIL ? 'email' : 'message'}
            label={t('Enable using {{contact}}', { contact: defaultMethod.contact })}
            onClick={onVerifySuccess}
            disabled={isLoading}
            css={css`
              margin-bottom: ${theme.spacing(2)};
            `}
            id='use-default-mfa-method-button'
          />
        )}
        <SelectMFAMethod hasMFAMethods={hasMFAMethods} />
        <div
          css={css`
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: ${theme.spacing(6)} 0;
          `}
        >
          <CheckboxField
            name='neverShowAgain'
            {...checkboxField}
            label={t("Don't show this again")}
            id='never-show-mfa-prompt-again'
            css={css`
              margin-top: ${theme.spacing(2)};
              margin-bottom: ${theme.spacing(2)};
              align-items: center;
              label {
                font-size: ${theme.font.size.small};
              }
            `}
          />
          <SkipButton id='mfa-prompt-skip-button' label={t('Skip and enable later')} onSkip={handleSkip} />
        </div>
      </MFAFormStep>

      <MFAFormStep currentStep={step} step='add'>
        <AddMFAAddress />
      </MFAFormStep>

      <MFAFormStep currentStep={step} step='verify'>
        <VerifyMFAAddress onVerifySuccess={onVerifySuccess} />
      </MFAFormStep>
    </section>
  );
}

// Uncomment and add in the steps above for testing the UI
// function CheatButton() {
//   const nextStep = useMFAContext((ctx) => ctx.nextStep);
//   return <button onClick={nextStep}>Next Step</button>;
// }
